import { useMutation } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import copy from "copy-to-clipboard";
import { compact } from "lodash";
import { forwardRef, memo, useMemo, useRef, useState } from "react";
import {
  AskAiChatMessageFragmentFragment,
  ChatSessionMessageReactionFragmentFragment,
  ChatSessionReactionType,
  ChatbotRoleEnum,
  CreateChatMessageReactionMutation,
  CreateChatMessageReactionMutationVariables,
} from "types/graphql-schema";

import { successNotificationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ErrorFallback from "@components/error-fallback/error-fallback";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { sanitizeMarkdown } from "@helpers/string";

import createChatMessageReactionMutation from "../graphql/create-chat-message-reaction-mutation";
import { getMessageCopyText } from "../helpers";
import AskAIMessageContent from "./ask-ai-message-content";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";
import AskAIToolCalls from "./ask-ai-tool-calls";

const AskAIMessageInner = forwardRef(
  (
    {
      message,
      chatSessionId,
      reaction,
      isLastMessage,
      isResponding,
      isAssessmentSession = false,
      className,
      style,
    }: {
      message: AskAiChatMessageFragmentFragment;
      chatSessionId: number | null;
      reaction: ChatSessionMessageReactionFragmentFragment | null;
      isLastMessage: boolean;
      isResponding: boolean;
      isAssessmentSession?: boolean;
      className?: string;
      style?: React.CSSProperties;
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const { isStreaming, content, imageUrls, documents } = message;
    const isUserMessage = message.role === ChatbotRoleEnum.User;

    const [showCommentBox, setShowCommentBox] = useState(false);
    const [comment, setComment] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [createChatReaction, { loading: isSubmittingReaction }] = useMutation<
      CreateChatMessageReactionMutation,
      CreateChatMessageReactionMutationVariables
    >(createChatMessageReactionMutation);

    const handleClickThumbsUp = () => {
      if (!chatSessionId) {
        return;
      }
      createChatReaction({
        variables: {
          chatSessionId,
          messageId: message.id,
          reactionType: ChatSessionReactionType.ThumbsUp,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Thank you for your feedback!" });
        },
      });
    };
    const handleClickThumbsDown = () => {
      if (!chatSessionId) {
        return;
      }
      setShowCommentBox(true);
    };

    const handleSubmitDislike = () => {
      if (!chatSessionId) {
        return;
      }
      createChatReaction({
        variables: {
          chatSessionId,
          messageId: message.id,
          reactionType: ChatSessionReactionType.ThumbsDown,
          comment: comment || undefined,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Thank you for your feedback!" });
          setShowCommentBox(false);
          setComment("");
        },
      });
    };

    const isThumbsUp =
      !!reaction && reaction.reactionType === ChatSessionReactionType.ThumbsUp;
    const isThumbsDown =
      !!reaction &&
      reaction.reactionType === ChatSessionReactionType.ThumbsDown;

    const markdownContent =
      (isStreaming ? sanitizeMarkdown(content) : content) || "";
    const shouldRenderMarkdownContent = isUserMessage && !!markdownContent;

    const toolCalls = compact(message.toolCalls || []);
    const slashCommandMatch = isUserMessage
      ? markdownContent.match(/^\/([a-z0-9][a-z0-9-]*)(\s[\s\S]*)?$/)
      : null;
    const slashCommandName = slashCommandMatch?.[1] ?? null;
    const slashCommandRest = slashCommandMatch?.[2]?.trimStart() ?? "";

    const copyText = useMemo(() => getMessageCopyText(content), [content]);
    const handleCopy = () => {
      copy(copyText);
      successNotificationVar({ title: "Copied to clipboard", timeout: 1000 });
    };

    return (
      <ErrorBoundary fallback={ErrorFallback}>
        <div
          className={classNames(
            !isLastMessage && "group/message-reaction",
            className,
          )}
          style={style}
          ref={ref}
        >
          {message.simulatedAt && (
            <time
              className="text-xs text-gray-500"
              dateTime={message.simulatedAt}
            >
              {message.simulatedAt} · simulated
            </time>
          )}
          {message.role === ChatbotRoleEnum.Assistant &&
            toolCalls.length > 0 && (
              <div className="mb-3">
                <AskAIToolCalls toolCalls={toolCalls} />
              </div>
            )}
          <div
            className={classNames(
              "w-full text-gray-700 flex",
              message.role === ChatbotRoleEnum.User
                ? "flex-col items-end gap-2 pl-12"
                : "flex-col gap-4",
            )}
            key={message.id}
            id={message.id}
          >
            {isUserMessage ? (
              <div className="flex max-w-full items-end gap-2">
                <div className="flex max-w-full flex-col items-end gap-2">
                  {shouldRenderMarkdownContent && (
                    <div
                      className={classNames(
                        "prose prose-sm max-w-none rounded-3xl bg-gray-100 py-2 px-4",
                        message.error && "text-red-600",
                      )}
                    >
                      {slashCommandName && (
                        <span className="font-mono text-blue-link">
                          /{slashCommandName}
                          {slashCommandRest && " "}
                        </span>
                      )}
                      {slashCommandName ? (
                        slashCommandRest && (
                          <AskAIMessageMarkdown>
                            {slashCommandRest}
                          </AskAIMessageMarkdown>
                        )
                      ) : (
                        <AskAIMessageMarkdown>
                          {markdownContent}
                        </AskAIMessageMarkdown>
                      )}
                    </div>
                  )}
                  {documents?.length > 0 && (
                    <div className="flex max-w-full flex-wrap justify-end gap-2">
                      {documents.map((document) => (
                        <a
                          key={document.url}
                          href={document.url}
                          target="_blank"
                          rel="noreferrer"
                          className="max-w-xs truncate rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-blue-link"
                          title={document.filename}
                        >
                          {document.filename}
                        </a>
                      ))}
                    </div>
                  )}
                  {imageUrls.length > 0 && (
                    <div className="flex max-w-full justify-end gap-2">
                      {imageUrls.map((imageUrl) => (
                        <a
                          key={imageUrl}
                          href={imageUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="block h-24 w-24 overflow-hidden rounded-md border border-gray-200"
                        >
                          <img
                            src={imageUrl}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                {message.sender && (
                  <Avatar user={message.sender} size="6" className="mb-1" />
                )}
              </div>
            ) : (
              <>
                {message.deliveryStatus === "failed" && (
                  <p className="text-sm text-gray-500">
                    The notification could not be delivered. This message is
                    still available here.
                  </p>
                )}
                {message.deliveryStatus === "unknown" && (
                  <p className="text-sm text-gray-500">
                    Notification delivery could not be confirmed.
                  </p>
                )}
                <AskAIMessageContent
                  content={content}
                  isStreaming={!!isStreaming}
                  chatSessionId={chatSessionId}
                  messageId={message.id || "tmp"}
                  canEditSuggestedAssessmentResponse={!isLastMessage}
                  isAssessmentSession={isAssessmentSession}
                />
              </>
            )}
          </div>
          {isUserMessage &&
            copyText &&
            !(isLastMessage && (isStreaming || isResponding)) && (
              <div
                className={classNames(
                  "flex gap-1 items-center mt-2 justify-end",
                  !isLastMessage &&
                    "opacity-0 group-hover/message-reaction:opacity-100",
                )}
              >
                <Button
                  icon={getTopicflowIcon("copy")}
                  onClick={handleCopy}
                  theme={buttonTheme.iconGray}
                  tooltip="Copy"
                  aria-label="Copy message"
                />
              </div>
            )}
          {message.role === ChatbotRoleEnum.Assistant &&
            !(isLastMessage && (isStreaming || isResponding)) && (
              <div
                className={classNames(
                  "flex gap-1 items-center mt-5 gap-2",
                  !isLastMessage &&
                    !reaction &&
                    "opacity-0 group-hover/message-reaction:opacity-100",
                )}
              >
                {copyText && (
                  <Button
                    icon={getTopicflowIcon("copy")}
                    onClick={handleCopy}
                    size={ButtonSize.small}
                    theme={buttonTheme.iconGray}
                    tooltip="Copy"
                    aria-label="Copy message"
                  />
                )}
                <Button
                  icon={getTopicflowIcon("thumbsUp")}
                  onClick={handleClickThumbsUp}
                  size={ButtonSize.small}
                  theme={buttonTheme.iconGray}
                  disabled={isThumbsUp || !!isStreaming}
                  className={classNames(
                    isThumbsUp && "opacity-100 text-gray-900",
                    isThumbsDown && "opacity-50 hover:opacity-100",
                  )}
                  aria-label="Thumbs up"
                />
                <Button
                  icon={getTopicflowIcon("thumbsDown")}
                  onClick={handleClickThumbsDown}
                  size={ButtonSize.small}
                  theme={buttonTheme.iconGray}
                  disabled={isThumbsDown || !!isStreaming}
                  className={classNames(
                    isThumbsDown && "opacity-100 text-gray-900",
                    isThumbsUp && "opacity-50 hover:opacity-100",
                  )}
                  aria-label="Thumbs down"
                />
              </div>
            )}
          {message.role === ChatbotRoleEnum.Assistant && showCommentBox && (
            <Modal
              open={showCommentBox}
              onClose={() => {
                setShowCommentBox(false);
                setComment("");
              }}
              alignedTop
              initialFocus={textareaRef}
            >
              <div className="p-6 flex flex-col gap-4">
                <ModalTitle
                  onClose={() => {
                    setShowCommentBox(false);
                    setComment("");
                  }}
                  className="text-sm"
                >
                  What could be improved?
                </ModalTitle>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Tell us what went wrong (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  ref={textareaRef}
                  aria-label="Feedback comment"
                />
                <div className="flex gap-2 justify-end items-center">
                  {isSubmittingReaction && <Loading mini size="5" />}
                  <Button
                    onClick={() => {
                      setShowCommentBox(false);
                      setComment("");
                    }}
                    theme={buttonTheme.default}
                    className="text-sm"
                    disabled={isSubmittingReaction}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitDislike}
                    theme={buttonTheme.primary}
                    className="text-sm"
                    disabled={isSubmittingReaction}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </ErrorBoundary>
    );
  },
);

// Memoized so a token arriving on the streaming (last) message does not
// re-render — and re-parse the ReactMarkdown of — every other message in the
// thread on each delta (TF-1577).
export const AskAIMessage = memo(AskAIMessageInner);

export default AskAIMessage;
